import { useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Heading } from '@/components/layout/Heading';
import { CodeBlock } from '@/components/marketing/CodeBlock';
import { PLATFORMS, type Platform } from '@/lib/db';
import { fadeUp } from '@/lib/motion';
import { getDownloadUrl, getSha256, type Release } from '@/lib/releases';
import { basenameFromUrl, verifyCommand } from './helpers';
import { PlatformIcon } from './PlatformIcon';

interface ChecksumCardProps {
  release: Release;
  platform: Platform;
}

export function ChecksumCard({ release, platform }: ChecksumCardProps) {
  const { t } = useTranslation('download');
  const [revealed, setRevealed] = useState(false);
  const url = getDownloadUrl(release, platform);
  const sha = getSha256(release, platform);
  const fileName = basenameFromUrl(url);
  const platformLabel = platform === PLATFORMS.macos ? t('os.macos') : t('os.windows');
  const verifyHeadline =
    platform === PLATFORMS.windows
      ? t('checksum.verify_headline_windows')
      : t('checksum.verify_headline_macos');
  const language = platform === PLATFORMS.windows ? 'powershell' : 'bash';

  if (!sha) return null;

  return (
    <motion.div
      variants={fadeUp}
      className="flex flex-col gap-5 rounded-2xl border border-border bg-card p-6 shadow-soft"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary-soft text-brand">
            <PlatformIcon platform={platform} className="h-4 w-4" />
          </span>
          <Heading level={4} as="h3">
            {platformLabel}
          </Heading>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {t('checksum.sha256_label')}
        </span>
      </div>

      {fileName ? (
        <div className="text-xs text-muted-foreground">
          <span className="me-2 uppercase tracking-widest">{t('checksum.filename_label')}</span>
          <span className="font-mono text-foreground">{fileName}</span>
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        {revealed ? (
          <>
            <CodeBlock code={sha} language="sha256" />
            <button
              type="button"
              onClick={() => setRevealed(false)}
              className="inline-flex items-center gap-2 self-start text-xs text-muted-foreground transition-colors duration-fast ease-out-quart hover:text-foreground"
            >
              <EyeOff className="h-3.5 w-3.5" aria-hidden="true" />
              {t('checksum.hide')}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setRevealed(true)}
            className="inline-flex items-center gap-2 self-start rounded-lg border border-border bg-background px-4 py-2 text-xs font-medium text-muted-foreground transition-colors duration-fast ease-out-quart hover:border-brand/50 hover:text-foreground"
          >
            <Eye className="h-3.5 w-3.5" aria-hidden="true" />
            {t('checksum.reveal')}
          </button>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {verifyHeadline}
        </p>
        <CodeBlock code={verifyCommand(platform, fileName)} language={language} />
      </div>
    </motion.div>
  );
}
